import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useOrders } from '@/hooks/useOrders';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { chime } from '@/lib/audio';
import { speak } from '@/lib/voice';
import { todayISO } from '@/lib/time';
import type { OrderDTO, OrderStatus } from '@/types/api';

/**
 * New-order alarm + voice announcements.
 *
 *   - On every new arrival (first time we see an order in pending/paid):
 *     speak the drink name once (if voiceAlertOn). The WS handler already
 *     played the initial chime, so we don't double-ring.
 *   - When alarmMode === 'persistent': re-ring + speak the oldest unattended
 *     order's drink name every 2s until the cashier accepts or cancels every
 *     new order.
 *
 * Uses the same `useOrders` query that powers the badge / OrdersPage, so
 * React Query dedupes the requests.
 */
const NEW_STATUSES: OrderStatus[] = ['pending', 'paid'];
// Chime-only ticks are quick, so 2 s feels urgent without being noisy.
// When voice is on, the announcement itself takes ~1.5 s, so we stretch
// to 4 s to avoid stepping on the previous utterance.
const CHIME_ONLY_INTERVAL_MS = 2000;
const VOICE_INTERVAL_MS = 4000;

export function useNewOrderAlarm() {
  const isAuthenticated = useAuthStore((s) => s.status === 'authenticated');
  const soundOn = useUIStore((s) => s.soundOn);
  const alarmMode = useUIStore((s) => s.alarmMode);
  const voiceAlertOn = useUIStore((s) => s.voiceAlertOn);
  const language = useUIStore((s) => s.language);
  const chimeVolume = useUIStore((s) => s.chimeVolume);
  const { t } = useTranslation();

  const today = todayISO();
  const { data } = useOrders(
    { date: today, limit: 200 },
    { enabled: isAuthenticated },
  );

  const newOrders = useMemo<OrderDTO[]>(
    () => (data?.items ?? []).filter((o) => NEW_STATUSES.includes(o.status)),
    [data],
  );

  // ── 1. Per-arrival voice announcement ───────────────────────────────────────
  const seenRef = useRef<Set<number>>(new Set());
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) return;

    // First load after sign-in: just record what's already there, don't
    // narrate every pending order from the start of the day.
    if (!hydratedRef.current) {
      seenRef.current = new Set(newOrders.map((o) => o.id));
      hydratedRef.current = true;
      return;
    }

    if (!soundOn || !voiceAlertOn) {
      seenRef.current = new Set(newOrders.map((o) => o.id));
      return;
    }

    const newcomers = newOrders.filter((o) => !seenRef.current.has(o.id));
    for (const o of newcomers) {
      speak(announcement(t('voice.newOrder'), o.drinkName), language, chimeVolume);
    }
    seenRef.current = new Set(newOrders.map((o) => o.id));
  }, [newOrders, isAuthenticated, soundOn, voiceAlertOn, language, chimeVolume, t]);

  // ── 2. Persistent re-ring + voice ───────────────────────────────────────────
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    const shouldRing =
      isAuthenticated && soundOn && alarmMode === 'persistent' && newOrders.length > 0;

    if (!shouldRing) {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    if (intervalRef.current !== null) return;
    const intervalMs = voiceAlertOn ? VOICE_INTERVAL_MS : CHIME_ONLY_INTERVAL_MS;
    intervalRef.current = window.setInterval(() => {
      chime();
      if (voiceAlertOn) {
        const oldest = newOrders[0];
        if (oldest) {
          speak(
            announcement(t('voice.newOrder'), oldest.drinkName),
            language,
            chimeVolume,
          );
        }
      }
    }, intervalMs);

    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    isAuthenticated,
    soundOn,
    alarmMode,
    voiceAlertOn,
    newOrders,
    language,
    chimeVolume,
    t,
  ]);
}

function announcement(prefix: string, drinkName: string): string {
  const name = drinkName?.trim();
  return name ? `${prefix} — ${name}` : prefix;
}
